const settledCollection = (result, source, warnings) => {
  if (result.status === 'fulfilled' && Array.isArray(result.value)) {
    return result.value;
  }

  warnings.push(`${source} details are unavailable`);
  return [];
};

const hasImageDescription = (image) => Boolean(
  String(image?.description || image?.alt || '').trim()
);

const mergeDescribedImages = (images, describedImages) => {
  const describedById = new Map(
    describedImages.filter((image) => image?.id).map((image) => [String(image.id), image])
  );
  let fallbackIndex = 0;

  return images.map((image) => {
    if (hasImageDescription(image)) return image;
    const described = image?.id ? describedById.get(String(image.id)) : describedImages[fallbackIndex];
    fallbackIndex += 1;
    return described ? { ...image, ...described } : image;
  });
};

export const loadPortfolioGenerationContext = async ({
  userId,
  includeProjects = true,
  includeSkills = true,
  includeJobs = true,
  includeImages = true,
  hydrateImageDescriptions = false,
  projectIds,
  skillIds,
  imageIds,
  loadProjects,
  loadSkills,
  loadJobs,
  loadImages,
  describeImages,
} = {}) => {
  const warnings = [];
  const [projectResult, skillResult, jobResult] = await Promise.allSettled([
    includeProjects ? loadProjects?.(userId) : [],
    includeSkills ? loadSkills?.(userId) : [],
    includeJobs ? loadJobs?.(userId) : [],
  ]);
  const filterRecords = (records, ids) => {
    if (!Array.isArray(ids)) return records;
    const requestedIds = new Set(ids.map(String));
    return records.filter((record) => record?.id && requestedIds.has(String(record.id)));
  };
  const context = {
    projects: includeProjects
      ? filterRecords(settledCollection(projectResult, 'Project', warnings), projectIds)
      : [],
    skills: includeSkills
      ? filterRecords(settledCollection(skillResult, 'Skill', warnings), skillIds)
      : [],
    jobs: includeJobs ? settledCollection(jobResult, 'Target job', warnings) : [],
    images: [],
    warnings,
  };

  if (!includeImages || typeof loadImages !== 'function') {
    return context;
  }

  try {
    const rawImages = await loadImages(userId);
    const requestedImageIds = Array.isArray(imageIds)
      ? new Set(imageIds.map(String))
      : null;
    context.images = (Array.isArray(rawImages) ? rawImages : []).filter((image) => (
      !requestedImageIds || (image?.id && requestedImageIds.has(String(image.id)))
    ));

    const missingDescriptions = context.images.filter((image) => !hasImageDescription(image));
    if (hydrateImageDescriptions && missingDescriptions.length && typeof describeImages === 'function') {
      try {
        const describedImages = await describeImages(missingDescriptions, userId);
        if (Array.isArray(describedImages)) {
          context.images = mergeDescribedImages(context.images, describedImages);
        }
      } catch (_) {
        warnings.push('Some image descriptions are unavailable');
      }
    }
  } catch (_) {
    warnings.push('Image details are unavailable');
  }

  return context;
};
